import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import isEqual from 'lodash/isEqual';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import Typography from 'src/components/ui/Typography';
import { styledComponent } from 'src/utils/styled';
import GooglePlacesAutocomplete from 'src/components/ui/GooglePlacesAutocomplete';
import { colours } from 'src/constants/colours';
import Select from 'src/components/ui/Select';
import Button from 'src/components/ui/Button';
import Icon from 'src/components/ui/Icon';
import { SearchCTAs } from './SearchCTAs';
import { breakpoints } from 'src/constants/breakpoints';
import { TotalAdsAndViewModeAndOrder } from './TotalAdsAndViewModeAndOrder';
import { useWindowSize } from '../hooks/useWindowSize';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useLoginModal from 'src/hooks/useLoginModal';
import { HousingRoomBudgetFilters } from './HousingRoomBudgetFilters';
import { AdSearchFormFilters } from './AdSearchFormFilters';
import { logEvent } from 'src/utils/firebase';
import { defaultQueryVariables } from 'src/constants/ad';
import { useAdSearchState } from '../contexts/AdSearchStateContext';
import { removeExtraParamsFromAdsQueryVariables } from '../utils/removeExtraParamsFromAdsQueryVariables';
import type { FormValues } from '../types/adSearchFormValues';
import type { ViewMode } from '../types/viewMode';

const FormContainer = styledComponent(Box)`
    background-color: ${colours.white.main};
    .MuiFormControl-root {
        margin: 0;
    }
    .MuiInputBase-root {
        height: 2.5rem;
    }
    .MuiInputBase-input {
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
    }
    .MuiButton-label {
        font-weight: 500;
        white-space: nowrap;
    }
    .MuiSelect-select:focus {
        background-color: transparent;
    }
    padding: 2rem 2rem 1.5rem 2rem;
    @media (width < ${breakpoints.desktopLarge}px) {
        padding: 1rem;
    }
    @media (width < ${breakpoints.desktopSmall}px) {
        padding: 2rem 2rem 1.5rem 2rem;
    }
    @media (width < ${breakpoints.tabletPortrait}px) {
        padding: 1rem;
    }
`;

const StyledGooglePlacesAutocomplete = styledComponent(GooglePlacesAutocomplete)`
    .MuiInputLabel-root {
        color: ${colours.primary.main}!important;
        font-size: 0.875rem;
        transform: translate(0.5rem, 0.6rem) scale(1)!important;
    }
`;

type Props = {
    initialValues: FormValues;
    isHybridView: boolean;
    viewMode: ViewMode;
    totalAds?: number;
    isSaveSearchModalOpen: boolean;
    handleCloseSaveSearchModal: () => void;
    handleClearFiltersClick: () => void;
    handleSearchClick: (values: FormValues) => void;
    handleToggleViewMode: () => void;
    handleOrderChange: (orderBy: string) => void;
    handleSaveSearch: () => void;
};

export const AdSearchForm: React.FC<Props> = ({
    initialValues,
    isHybridView,
    viewMode,
    totalAds,
    isSaveSearchModalOpen,
    handleCloseSaveSearchModal,
    handleClearFiltersClick,
    handleSearchClick,
    handleToggleViewMode,
    handleOrderChange,
    handleSaveSearch,
}) => {
    const { t } = useTranslation(['ad', 'common']);
    const { logEventDynamic } = useDynamicLogEvents();
    const { createAuthenticatedHandler } = useLoginModal();
    const {
        selectedSavedSearch,
        adsQueryVariables,
        isPolygonDisplayed,
        isSavedSearchListModalOpen,
        changeAdSearchState,
        resetAdSearchState,
    } = useAdSearchState();
    const [filtersPanelIsOpen, setFiltersPanelIsOpen] = useState<boolean>(false);

    const { isMobile, isTabletPortrait, isTabletLandscape, isDesktopSmall } = useWindowSize();

    const actualQueryVariablesAndSavedSearchAreSame = useMemo(() => {
        const queryVariablesWithoutExtraParams = removeExtraParamsFromAdsQueryVariables(adsQueryVariables);

        if (
            queryVariablesWithoutExtraParams.place?.geometry === undefined &&
            queryVariablesWithoutExtraParams.place?.googlePlaceId === undefined
        ) {
            delete queryVariablesWithoutExtraParams.place?.geometry;
            delete queryVariablesWithoutExtraParams.place?.googlePlaceId;
        }

        const defaultQueryVariablesWithoutExtraParams = removeExtraParamsFromAdsQueryVariables(defaultQueryVariables);

        const selectedSearchParamsAndActualAreSame =
            selectedSavedSearch &&
            isEqual(queryVariablesWithoutExtraParams, {
                ...defaultQueryVariablesWithoutExtraParams,
                ...JSON.parse(selectedSavedSearch.parameters),
            });
        const selectedSearchPlaceAndActualAreSame =
            selectedSavedSearch?.placeGeometry &&
            isEqual(adsQueryVariables.places, [
                {
                    type: selectedSavedSearch.placeGeometry.type,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    coordinates: selectedSavedSearch.placeGeometry.coordinates,
                },
            ]);
        return Boolean(selectedSearchParamsAndActualAreSame && selectedSearchPlaceAndActualAreSame);
    }, [adsQueryVariables, selectedSavedSearch]);

    const displaySaveSearchButtonsOnSingleRow = useMemo(
        () => (isHybridView && isDesktopSmall) || isMobile,
        [isDesktopSmall, isHybridView, isMobile],
    );

    const shouldDisplayHousingTypesRoomsAndBudgetInAdditionalFilters = useMemo(
        () => displaySaveSearchButtonsOnSingleRow || isTabletLandscape || isTabletPortrait,
        [displaySaveSearchButtonsOnSingleRow, isTabletLandscape, isTabletPortrait],
    );

    return (
        // Formik is used here to avoid errors using useField in children components
        <Formik initialValues={initialValues} onSubmit={console.info} enableReinitialize>
            {({ values, setFieldValue, dirty }: FormikProps<FormValues>) => {
                const searchesCTAs = (
                    <SearchCTAs
                        isSaveSearchButtonDisabled={actualQueryVariablesAndSavedSearchAreSame}
                        saveSearchButtonFullWith={displaySaveSearchButtonsOnSingleRow}
                        onSaveSearchClick={() => createAuthenticatedHandler(handleSaveSearch)}
                        onSavedSearchesClick={() => {
                            logEventDynamic(AnalyticsEvent.DisplaySavedSearches);
                            return changeAdSearchState({ isSavedSearchListModalOpen: true });
                        }}
                        isSaveSearchModalOpen={isSaveSearchModalOpen}
                        handleCloseSaveSearchModal={handleCloseSaveSearchModal}
                        isSavedSearchesModalOpen={isSavedSearchListModalOpen}
                        handleSavedSearchesModalClose={() => changeAdSearchState({ isSavedSearchListModalOpen: false })}
                    />
                );

                return (
                    <>
                        <FormContainer>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                paddingBottom="0.75rem"
                            >
                                <Box
                                    flex="1"
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent={isMobile ? 'space-between' : 'flex-start'}
                                    width="50%"
                                >
                                    <Box display="flex" alignItems="center" py={1.5} width="100%">
                                        <Typography
                                            data-target="find-property"
                                            data-target-id="find-property"
                                            variant="h2"
                                            serif
                                            noWrap
                                        >
                                            {selectedSavedSearch?.name ?? t('ad:findProperty')}
                                        </Typography>
                                    </Box>
                                </Box>
                                {!displaySaveSearchButtonsOnSingleRow && (
                                    <Box display="flex" my={1}>
                                        {searchesCTAs}
                                    </Box>
                                )}
                            </Box>
                            <Grid spacing={1} container>
                                {displaySaveSearchButtonsOnSingleRow && (
                                    <Grid xs={12} lg={12} item>
                                        {searchesCTAs}
                                    </Grid>
                                )}
                                <Grid xs={8} lg={isHybridView ? 8 : 4} item>
                                    <StyledGooglePlacesAutocomplete
                                        name="place"
                                        label={t(isPolygonDisplayed ? 'ad:customBoundary' : 'ad:mapZone')}
                                        onChangeOption={(option) => {
                                            setFieldValue('places', option?.geometry ? [option.geometry] : undefined);
                                        }}
                                        heightVariant="small"
                                        includeGeometry
                                        onlyFrance
                                    />
                                </Grid>
                                <Grid xs={4} lg={isHybridView ? 4 : 2} item>
                                    <Select
                                        name="type"
                                        items={[
                                            {
                                                label: t('ad:buy'),
                                                value: 'sell',
                                            },
                                            {
                                                label: t('ad:rent'),
                                                value: 'rent',
                                            },
                                        ]}
                                        InputProps={{
                                            inputProps: {
                                                'data-target': 'ad-type',
                                                'data-target-id': 'ad-type',
                                            },
                                        }}
                                    />
                                </Grid>
                                {!shouldDisplayHousingTypesRoomsAndBudgetInAdditionalFilters && (
                                    <HousingRoomBudgetFilters
                                        searchType={values.type}
                                        queryVariables={adsQueryVariables}
                                        xs={6}
                                        lg={isHybridView ? 4 : 2}
                                    />
                                )}
                                <Grid xs={12} item>
                                    <AdSearchFormFilters
                                        open={filtersPanelIsOpen}
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        queryVariables={adsQueryVariables}
                                        showHousingRoomBudgetFilters={
                                            shouldDisplayHousingTypesRoomsAndBudgetInAdditionalFilters
                                        }
                                        isHybridView={isHybridView}
                                    />
                                </Grid>
                                <Grid xs={12} item>
                                    <Box display="flex" justifyContent="space-between" gap="1rem" flexWrap="wrap">
                                        <Box display="flex">
                                            <Button
                                                data-target="filters-more"
                                                data-target-id="filters-more"
                                                onClick={() => {
                                                    setFiltersPanelIsOpen(!filtersPanelIsOpen);
                                                    if (!filtersPanelIsOpen)
                                                        logEventDynamic(AnalyticsEvent.MoreFilters);
                                                }}
                                                startIcon={
                                                    filtersPanelIsOpen ? <Icon name="Close" /> : <Icon name="Filter" />
                                                }
                                                color="white"
                                                variant="outlined"
                                                sx={{ height: '2.5rem', padding: '0.75rem' }}
                                            >
                                                {t('ad:filters')}
                                            </Button>
                                            <Button
                                                onClick={handleClearFiltersClick}
                                                variant="text"
                                                size="large"
                                                disabled={actualQueryVariablesAndSavedSearchAreSame}
                                            >
                                                <Typography fontWeight="bold">{t('ad:resetFilters')}</Typography>
                                            </Button>
                                        </Box>
                                        <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
                                            <Box display="flex" justifyContent="flex-end" alignItems="center">
                                                {Boolean(selectedSavedSearch) && (
                                                    <Button variant="outlined" onClick={resetAdSearchState}>
                                                        {t('ad:newSearch')}
                                                    </Button>
                                                )}
                                                <Box ml="0.5rem">
                                                    <Button
                                                        onClick={() => {
                                                            handleSearchClick(values);
                                                            setFiltersPanelIsOpen(false);
                                                            logEvent(AnalyticsEvent.ClickEstateSearch);
                                                        }}
                                                        color="primary"
                                                        disabled={!dirty}
                                                    >
                                                        {t(`common:search`)}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </FormContainer>
                        <TotalAdsAndViewModeAndOrder
                            totalAds={totalAds}
                            viewMode={viewMode}
                            isHybridView={isHybridView}
                            handleToggleViewMode={handleToggleViewMode}
                            handleOrderChange={handleOrderChange}
                        />
                    </>
                );
            }}
        </Formik>
    );
};
